import type { CSSProperties } from 'react';

import classNames from 'classnames';

import type {
  AssetsTheme,
  AssetsThemeTypography,
} from '@iconik-io/iconik-api/src/services/assets';

import { isPortalFontFamily, resolvePortalFontStack } from './portal_fonts';

import styles from './portal_theme.module.css';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** Luminance above which a color needs dark rather than light text on it. */
const LIGHT_COLOR_LUMINANCE = 0.179;

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR.test(value);
}

function getLuminance(hex: string): number {
  const packed = parseInt(hex.slice(1), 16);
  const [red, green, blue] = [
    (packed >> 16) & 255,
    (packed >> 8) & 255,
    packed & 255,
  ].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return red * 0.2126 + green * 0.7152 + blue * 0.0722;
}

function mix(hex: string, amount: number, towards: string): string {
  return `color-mix(in srgb, ${hex} ${amount}%, ${towards})`;
}

/** Label color that stays legible on a fill of the given luminance. */
function getLabelToken(isLight: boolean): string {
  return isLight
    ? 'var(--color-linear-neutral-10)'
    : 'var(--color-linear-neutral-86)';
}

function getPrimaryTokens(primary: string): CSSProperties {
  const isLight = getLuminance(primary) > LIGHT_COLOR_LUMINANCE;
  // Shift the interaction states away from the chosen color so a light brand
  // color darkens rather than washing out.
  const away = isLight ? 'black' : 'white';
  return {
    '--color-primary-default': primary,
    '--color-primary-hover': mix(primary, 92, away),
    // The pressed state reads `--color-primary-focused`; there is no
    // `--color-primary-pressed` token.
    '--color-primary-focused': mix(primary, 84, away),
    '--color-primary-label': getLabelToken(isLight),
  };
}

function getSecondaryTokens(secondary: string): CSSProperties {
  const isLight = getLuminance(secondary) > LIGHT_COLOR_LUMINANCE;
  const away = isLight ? 'black' : 'white';
  return {
    '--color-secondary-default': secondary,
    '--color-secondary-hover': mix(secondary, 92, away),
    // Unlike primary, secondary has a real pressed token.
    '--color-secondary-pressed': mix(secondary, 84, away),
    '--color-secondary-label': getLabelToken(isLight),
  };
}

/**
 * Raised surfaces are derived rather than left on the base theme, so cards and
 * the cover still read as raised against any chosen background.
 *
 * UAD's own ramp lifts them in both themes (dark 10 → 14 → 20, light 88 → 92 →
 * 96), but a near-white background has no headroom to lift into, so they step
 * away from the background's own luminance instead.
 */
function getBackgroundTokens(background: string): CSSProperties {
  const isLight = getLuminance(background) > LIGHT_COLOR_LUMINANCE;
  const away = isLight ? 'black' : 'white';
  return {
    '--color-surfaces-body': background,
    '--color-surfaces-surface-1': mix(background, 94, away),
    '--color-surfaces-surface-2': mix(background, 88, away),
  };
}

export function getPortalThemeClass(
  base: AssetsTheme['base'],
): 'theme_dark' | 'theme_light' {
  return base === 'light' ? 'theme_light' : 'theme_dark';
}

/**
 * Only the roles that actually carry a family get a class, so an unset role
 * keeps UAD's own `--font-*` shorthand untouched.
 */
export function getPortalTypographyClass(
  typography: AssetsThemeTypography | null | undefined,
): string | undefined {
  return (
    classNames({
      [styles.primary_font]: isPortalFontFamily(
        typography?.heading_font_family,
      ),
      [styles.secondary_font]: isPortalFontFamily(typography?.font_family),
    }) || undefined
  );
}

/**
 * Maps a portal's theme colors and fonts onto custom properties, to be
 * applied on the same element that carries the theme class. `colors.text` is
 * surfaced as the text accent.
 *
 * They have to be inline rather than a CSS Module class: the values are runtime
 * data, and CSS has no valid way to fall back to a token's own themed value
 * (`--x: var(--y, var(--x))` is self-referential), so "only override what the
 * author actually set" has to be decided here. Returns `undefined` when nothing
 * is overridden.
 */
/**
 * Only custom properties: the portal theme recolors the token layer rather than
 * setting CSS properties directly, and CSSProperties cannot name a `--*` key.
 */
export type PortalThemeStyle = CSSProperties &
  Record<`--${string}`, string | undefined>;

export function getPortalThemeStyle(
  theme: AssetsTheme | null | undefined,
): PortalThemeStyle | undefined {
  const { background, primary, secondary, text: accent } = theme?.colors ?? {};
  const { heading_font_family: primaryFont, font_family: secondaryFont } =
    theme?.typography ?? {};
  const hasBackground = isHexColor(background);
  const hasPrimary = isHexColor(primary);
  const hasSecondary = isHexColor(secondary);
  const hasAccent = isHexColor(accent);
  const hasPrimaryFont = isPortalFontFamily(primaryFont);
  const hasSecondaryFont = isPortalFontFamily(secondaryFont);

  if (
    !hasBackground &&
    !hasPrimary &&
    !hasSecondary &&
    !hasAccent &&
    !hasPrimaryFont &&
    !hasSecondaryFont
  ) {
    return undefined;
  }

  return {
    ...(hasBackground ? getBackgroundTokens(background) : undefined),
    ...(hasPrimary ? getPrimaryTokens(primary) : undefined),
    ...(hasSecondary ? getSecondaryTokens(secondary) : undefined),
    // The text accent recolors high emphasis only: body copy stays on the base
    // theme's medium emphasis so the accent reads as emphasis, not as the
    // portal's text color. It is published under its own name as well, so
    // the cover can tell an unset accent from a set one and fall back to a
    // value that survives its dark scrim.
    ...(hasAccent
      ? {
          '--portal-text-accent': accent,
          '--color-emphasis-high-emphasis-content': accent,
        }
      : undefined),
    ...(hasPrimaryFont
      ? { '--portal-primary-font': resolvePortalFontStack(primaryFont) }
      : undefined),
    ...(hasSecondaryFont
      ? {
          '--portal-secondary-font': resolvePortalFontStack(secondaryFont),
        }
      : undefined),
  };
}
